import React, { useState, useEffect, useRef } from 'react';
import { useCookieConsent } from '../context/CookieConsentContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function CookieBanner() {
  const { t } = useTranslation();
  const { showBanner, showCustom, setShowCustom, acceptAll, rejectAll, savePrefs, prefs } = useCookieConsent();

  // Local toggle state for granular preferences (mirrors stored prefs or defaults)
  const [localPrefs, setLocalPrefs] = useState({
    analytics: prefs?.analytics ?? false,
    marketing: prefs?.marketing ?? false,
  });

  // Sync local prefs when banner opens in custom mode
  useEffect(() => {
    if (showCustom && prefs) {
      setLocalPrefs({ analytics: prefs.analytics ?? false, marketing: prefs.marketing ?? false });
    }
  }, [showCustom, prefs]);

  // Focus trap ref
  const bannerRef = useRef(null);

  // Escape key → reject all
  useEffect(() => {
    if (!showBanner) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        toast.success(t('cookies.toastSaved'));
        rejectAll();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showBanner, rejectAll]);

  // Focus first element when banner opens
  useEffect(() => {
    if (showBanner && bannerRef.current) {
      const firstBtn = bannerRef.current.querySelector('button');
      if (firstBtn) firstBtn.focus();
    }
  }, [showBanner]);

  if (!showBanner) return null;

  const handleAcceptAll = () => { acceptAll(); toast.success(t('cookies.toastSaved')); };
  const handleRejectAll = () => { rejectAll(); toast.success(t('cookies.toastSaved')); };
  const handleSaveCustom = () => {
    savePrefs({ necessary: true, analytics: localPrefs.analytics, marketing: localPrefs.marketing });
    toast.success(t('cookies.toastSaved'));
  };

  // ─── OVERLAY ──────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.65)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding: '0 0 24px 0',
      }}
      role="dialog"
      aria-label="Préférences cookies"
      aria-modal="true"
    >
      {/* ─── PANEL ─────────────────────────────────────────────────────────── */}
      <div
        ref={bannerRef}
        style={{
          background: '#1a1929',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '16px',
          padding: '28px 28px 24px',
          maxWidth: '480px',
          width: 'calc(100% - 32px)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
          animation: 'slideUpCookie 0.3s ease',
        }}
      >
        <style>{`
          @keyframes slideUpCookie {
            from { opacity: 0; transform: translateY(24px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .cookie-toggle { position:relative; width:40px; height:22px; flex-shrink:0; cursor:pointer; }
          .cookie-toggle input { opacity:0; width:0; height:0; }
          .cookie-slider {
            position:absolute; inset:0; background:#333; border-radius:22px; transition:0.25s;
          }
          .cookie-slider:before {
            content:""; position:absolute; height:16px; width:16px; left:3px; bottom:3px;
            background:#fff; border-radius:50%; transition:0.25s;
          }
          .cookie-toggle input:checked + .cookie-slider { background:#ff6b6b; }
          .cookie-toggle input:checked + .cookie-slider:before { transform:translateX(18px); }
          .cookie-toggle input:disabled + .cookie-slider { background:#20c997; cursor:not-allowed; opacity:0.8; }
        `}</style>

        {/* ─── MAIN BANNER ──────────────────────────────────────────────── */}
        {!showCustom ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <h2 style={{ color: '#fff', fontSize: '17px', fontWeight: 700, margin: 0 }}>
                {t('cookies.bannerTitle')}
              </h2>
            </div>
            <p style={{ color: '#a0a0ab', fontSize: '13px', lineHeight: '1.6', marginBottom: '20px' }}>
              {t('cookies.bannerText')}{' '}
              <a href="/cookies" style={{ color: '#ff6b6b', textDecoration: 'underline' }}>{t('cookies.learnMore')}</a>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={handleAcceptAll}
                data-testid="cookie-accept-all"
                style={{
                  background: '#ff6b6b', color: '#fff', border: 'none', borderRadius: '10px',
                  padding: '12px 0', fontWeight: 700, fontSize: '14px', cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseOver={e => e.target.style.background = '#ff5555'}
                onMouseOut={e => e.target.style.background = '#ff6b6b'}
              >
                {t('cookies.acceptAll')}
              </button>
              <button
                onClick={handleRejectAll}
                data-testid="cookie-reject-all"
                style={{
                  background: 'rgba(255,255,255,0.06)', color: '#e8e8e8', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '10px', padding: '12px 0', fontWeight: 600, fontSize: '14px', cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseOver={e => e.target.style.background = 'rgba(255,255,255,0.1)'}
                onMouseOut={e => e.target.style.background = 'rgba(255,255,255,0.06)'}
              >
                {t('cookies.rejectAll')}
              </button>
              <button
                onClick={() => setShowCustom(true)}
                data-testid="cookie-customize"
                style={{
                  background: 'transparent', color: '#a0a0ab', border: 'none',
                  padding: '8px 0', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                {t('cookies.customize')}
              </button>
            </div>
          </>
        ) : (
          /* ─── GRANULAR PREFERENCES ─────────────────────────────────────── */
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <span style={{ fontSize: '18px' }}>⚙️</span>
              <h2 style={{ color: '#fff', fontSize: '16px', fontWeight: 700, margin: 0 }}>
                {t('cookies.prefsTitle')}
              </h2>
            </div>

            {/* Necessary */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ flex: 1, marginRight: '12px' }}>
                <p style={{ color: '#e8e8e8', fontSize: '13px', fontWeight: 600, margin: '0 0 2px' }}>{t('cookies.necessary')}</p>
                <p style={{ color: '#a0a0ab', fontSize: '11px', margin: 0 }}>{t('cookies.necessaryDesc')}</p>
              </div>
              <label className="cookie-toggle">
                <input type="checkbox" checked disabled />
                <span className="cookie-slider" />
              </label>
            </div>

            {/* Analytics */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ flex: 1, marginRight: '12px' }}>
                <p style={{ color: '#e8e8e8', fontSize: '13px', fontWeight: 600, margin: '0 0 2px' }}>Analytics (Mixpanel)</p>
                <p style={{ color: '#a0a0ab', fontSize: '11px', margin: 0 }}>{t('cookies.analyticsDesc')}</p>
              </div>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={localPrefs.analytics}
                  onChange={e => setLocalPrefs(p => ({ ...p, analytics: e.target.checked }))}
                  data-testid="cookie-toggle-analytics"
                />
                <span className="cookie-slider" />
              </label>
            </div>

            {/* Marketing */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', marginBottom: '16px' }}>
              <div style={{ flex: 1, marginRight: '12px' }}>
                <p style={{ color: '#e8e8e8', fontSize: '13px', fontWeight: 600, margin: '0 0 2px' }}>{t('cookies.marketing')}</p>
                <p style={{ color: '#a0a0ab', fontSize: '11px', margin: 0 }}>{t('cookies.marketingDesc')}</p>
              </div>
              <label className="cookie-toggle">
                <input
                  type="checkbox"
                  checked={localPrefs.marketing}
                  onChange={e => setLocalPrefs(p => ({ ...p, marketing: e.target.checked }))}
                  data-testid="cookie-toggle-marketing"
                />
                <span className="cookie-slider" />
              </label>
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowCustom(false)}
                data-testid="cookie-back"
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.06)', color: '#e8e8e8',
                  border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px',
                  padding: '11px 0', fontSize: '13px', cursor: 'pointer',
                }}
              >
                ← {t('cookies.back')}
              </button>
              <button
                onClick={handleSaveCustom}
                data-testid="cookie-save-prefs"
                style={{
                  flex: 2, background: '#ff6b6b', color: '#fff', border: 'none',
                  borderRadius: '10px', padding: '11px 0', fontWeight: 700, fontSize: '13px', cursor: 'pointer',
                }}
              >
                {t('cookies.savePreferences')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
